import { printReceiptText } from 'containers/OutletModule/Home/TableSubmenu/BillSettlement/BillReceipt/BillReceiptFunctions';
import { printGuestOrderSlipUSBPOS } from 'containers/OutletModule/Home/TableSubmenu/POSPrinter/BillReceipt/posBillReceiptPrinterUSB';
import { closeUsbPrinter, openPairedUsbPrinter } from 'helpers/webUsbEscPos';
import {
	usesDirectWebUsb,
	usesPrinterAgentUsb,
} from 'helpers/receiptPrinterTransport';
import { printOrderReceiptRawViaLocalAgent } from 'services/localPrintAgentReceipt';

function isIpAddress(value: unknown): boolean {
	return /^\d{1,3}(\.\d{1,3}){3}$/.test(String(value ?? '').trim());
}

/** Same target print bill uses for a network Epson on the local printer agent. */
export function receiptAgentPrinterName(printer?: {
	isSDK?: boolean | null;
	connectType?: string | null;
	usePrinterAgent?: boolean | null;
	name?: string | null;
	deviceIP?: string | null;
} | null): string | null {
	if (!printer || printer.isSDK === true) return null;
	if (usesPrinterAgentUsb(printer)) return printer.name || null;
	const ip = String(printer.deviceIP ?? '').trim();
	if (isIpAddress(ip)) return ip;
	if (String(printer.connectType ?? '').toUpperCase() === 'NETWORK' && ip) {
		return ip;
	}
	return null;
}

export async function printTableOrderSlip(input: {
	receiptPrinter?: any;
	outletName?: string;
	orderData?: any;
	tableData?: any;
	orderItemData?: any[];
	orderMode?: string;
	voidQuantity?: number;
	reasonCode?: string;
	remark?: string;
}): Promise<void> {
	const { receiptPrinter, ...slip } = input;
	if (!receiptPrinter) {
		console.warn('Table order print skipped: no default receipt printer.');
		return;
	}

	const width = receiptPrinter.deviceAlignmentCode;
	const connectType = String(receiptPrinter.connectType ?? '').toUpperCase();

	// Bixolon SDK: same requestPrint call as print bill, using the receipt printer name.
	if (receiptPrinter.isSDK === true) {
		const printerName = String(receiptPrinter.name ?? '').trim();
		if (!printerName) {
			console.warn('Table order print skipped: Bixolon printer has no name.');
			return;
		}
		await printReceiptText(
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			printerName,
			'server',
			null,
			'guest-order-receipt',
			null,
			slip.outletName,
			slip.orderData,
			slip.tableData,
			slip.orderItemData,
			slip.orderMode,
			null,
			slip.voidQuantity,
			slip.reasonCode,
			slip.remark,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
		);
		return;
	}

	// Zadig USB: same transferOut path as print bill and print day end. Not the printer agent.
	if (usesDirectWebUsb(receiptPrinter)) {
		const device = await openPairedUsbPrinter(receiptPrinter.deviceInfo);
		try {
			await printGuestOrderSlipUSBPOS(device, { ...slip, width });
		} finally {
			await closeUsbPrinter(device);
		}
		return;
	}

	// Network Epson: printer agent by IP only, same as print bill. Never a Windows/Zadig name.
	if (connectType === 'NETWORK') {
		const printerName = String(receiptPrinter.deviceIP ?? '').trim();
		if (!isIpAddress(printerName)) {
			console.warn(
				'Table order print skipped: network receipt printer has no IP.',
			);
			return;
		}
		await printOrderReceiptRawViaLocalAgent({
			...slip,
			printerName,
			receiptType: 'guest-order-receipt',
			width,
		});
		return;
	}

	const printerName = receiptAgentPrinterName(receiptPrinter);
	if (!printerName) {
		console.warn(
			'Table order print skipped: receipt printer has no network IP or agent name.',
		);
		return;
	}

	await printOrderReceiptRawViaLocalAgent({
		...slip,
		printerName,
		receiptType: 'guest-order-receipt',
		width,
	});
}

function kitchenPrinterIp(printer?: {
	address?: string | null;
	deviceIP?: string | null;
} | null): string | null {
	const address = String(printer?.address ?? '').trim();
	if (isIpAddress(address)) return address;
	const deviceIP = String(printer?.deviceIP ?? '').trim();
	if (isIpAddress(deviceIP)) return deviceIP;
	return null;
}

function kitchenPrintKey(input: {
	orderID?: string;
	orderItemIDs?: string[];
	orderMode?: string;
	voidQuantity?: number;
}): string {
	const ids = [...(input.orderItemIDs || [])]
		.filter(Boolean)
		.map(String)
		.sort()
		.join(',');
	return `menux-kitchen-printed:${input.orderID ?? ''}:${input.orderMode ?? ''}:${
		input.voidQuantity ?? ''
	}:${ids}`;
}

/** Remember that this screen already sent the kitchen slip, so the other tab does not print it again. */
export function markKitchenPrintedLocally(input: {
	orderID?: string;
	orderItemIDs?: string[];
	orderMode?: string;
	voidQuantity?: number;
}): void {
	try {
		localStorage.setItem(kitchenPrintKey(input), String(Date.now()));
	} catch {
		/* storage can be unavailable */
	}
}

export function kitchenPrintedLocally(input: {
	orderID?: string;
	orderItemIDs?: string[];
	orderMode?: string;
	voidQuantity?: number;
}): boolean {
	try {
		const raw = localStorage.getItem(kitchenPrintKey(input));
		if (!raw) return false;
		return Date.now() - Number(raw) < 20000;
	} catch {
		return false;
	}
}

/** Bixolon SDK prompts the kitchen printer name. Non-SDK uses the printer agent by IP. */
async function printKitchenSlipViaBixolon(
	printerName: string,
	input: {
		outletName?: string;
		orderData?: any;
		tableData?: any;
		orderMode?: string;
		voidQuantity?: number;
		reasonCode?: string;
		remark?: string;
	},
	items: any[],
): Promise<void> {
	await printReceiptText(
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		printerName,
		'server',
		null,
		'order-receipt',
		null,
		input.outletName,
		input.orderData,
		input.tableData,
		items,
		input.orderMode,
		null,
		input.voidQuantity,
		input.reasonCode,
		input.remark,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
		null,
	);
}

/** Kitchen slip for each item's kitchen printer, on this PC. */
export async function printKitchenSlips(input: {
	outletName?: string;
	orderData?: any;
	tableData?: any;
	orderItemData?: any[];
	orderMode?: string;
	voidQuantity?: number;
	reasonCode?: string;
	remark?: string;
}): Promise<void> {
	const groups = new Map<
		string,
		{ printer: any; items: any[]; target: string; sdk: boolean }
	>();
	for (const item of input.orderItemData || []) {
		const printer = item?.menuItem?.kitchenPrinter;
		if (printer?.isSDK === true) {
			const name = printer?.name || 'NonKitchenPrinter';
			const key = `sdk:${name}`;
			const group = groups.get(key) || {
				printer,
				items: [],
				target: name,
				sdk: true,
			};
			group.items.push(item);
			groups.set(key, group);
			continue;
		}
		const ip = kitchenPrinterIp(printer);
		if (!ip) {
			console.warn(
				'Kitchen print skipped: kitchen printer has no IP.',
				printer?.name ?? '',
			);
			continue;
		}
		const group = groups.get(ip) || {
			printer,
			items: [],
			target: ip,
			sdk: false,
		};
		group.items.push(item);
		groups.set(ip, group);
	}

	for (const group of groups.values()) {
		if (group.sdk) {
			await printKitchenSlipViaBixolon(group.target, input, group.items);
			continue;
		}
		await printOrderReceiptRawViaLocalAgent({
			outletName: input.outletName,
			orderData: input.orderData,
			tableData: input.tableData,
			orderItemData: group.items,
			orderMode: input.orderMode,
			voidQuantity: input.voidQuantity,
			reasonCode: input.reasonCode,
			remark: input.remark,
			printerName: group.target,
			receiptType: 'order-receipt',
			width: group.printer?.deviceAlignmentCode,
		});
	}
}
